/**
 * MapNotesLayer — Field communication & issue tracking for construction maps.
 * Status workflow: open → assigned → in_progress → complete → verified
 * Features: 22 pin style presets, animated pins, deadline rings, visibility controls,
 * comment threads, read receipts, activity log, resolution photo proof.
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Marker, InfoWindow, OverlayView } from '@react-google-maps/api';
import { calculateDistance } from '../../services/gpsService';
import { uploadNotePhoto, deleteNotePhoto, loadNoteReplies, createNoteReply, markNoteRead, loadNoteReads, loadNoteActivity, editMapNote } from '../../services/mapService';
import { PIN_PRESETS, PIN_STYLE_GROUPS, getPreset, isCustomStyle, getCustomColor, getDeadlineRing, getAllKeyframes, buildPresetPinSvg, buildAnimatedPinHtml } from '../../config/pinPresets';

const STATUS_COLORS = {
  open: '#F44336',
  assigned: '#FF9800',
  in_progress: '#2196F3',
  complete: '#009688',
  verified: '#4CAF50',
};

const CATEGORY_LETTERS = {
  general: 'G',
  issue: '!',
  safety: 'S',
  question: '?',
  instruction: 'I',
};

const CATEGORIES = ['general', 'issue', 'safety', 'question', 'instruction'];
const PRIORITIES = ['low', 'normal', 'high', 'critical'];
const CREW_TYPES = ['Boring Plow', 'Trencher', 'Puller', 'Splicer', 'All'];

const CATEGORY_LABELS = {
  general: 'General',
  issue: 'Issue',
  safety: 'Safety',
  question: 'Question',
  instruction: 'Instruction',
};

const STATUS_LABELS = {
  open: 'Open',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  complete: 'Complete',
  verified: 'Verified',
};

const PRIORITY_COLORS = {
  low: '#9E9E9E',
  normal: '#607D8B',
  high: '#FF9800',
  critical: '#F44336',
};

const VISIBILITY_OPTIONS = [
  { value: 'shared', label: 'Everyone' },
  { value: 'admin', label: 'Admin Only' },
  { value: 'inhouse', label: 'In-House Only' },
  { value: 'contractor', label: 'Specific Contractor' },
];

function buildPinSvg(status, category, priority, pinStyle, deadline) {
  // If pinStyle is set, use preset/custom rendering
  if (pinStyle) {
    const presetSvg = buildPresetPinSvg(pinStyle, category, deadline);
    if (presetSvg) return presetSvg;
  }

  const bodyColor = STATUS_COLORS[status] || STATUS_COLORS.open;
  const letter = CATEGORY_LETTERS[category] || 'G';
  const isVerified = status === 'verified';
  const opacity = isVerified ? '0.45' : '1';

  // Priority ring for critical/high
  let ring = '';
  if (priority === 'critical') {
    ring = `<circle cx='14' cy='14' r='13' fill='none' stroke='#F44336' stroke-width='2.5' opacity='0.8'/>`;
  } else if (priority === 'high') {
    ring = `<circle cx='14' cy='14' r='13' fill='none' stroke='#FF9800' stroke-width='2' opacity='0.7'/>`;
  }

  // Deadline ring (only if no pinStyle — pinStyle pins get deadline ring via buildPresetPinSvg)
  let deadlineRing = '';
  if (deadline) {
    const dlRing = getDeadlineRing(deadline);
    if (dlRing && dlRing.color) {
      deadlineRing = `<circle cx='16' cy='16' r='15' fill='none' stroke='${dlRing.color}' stroke-width='${dlRing.width || 2}' opacity='0.85'/>`;
    }
  }

  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='40' opacity='${opacity}'>
    ${deadlineRing}${ring}
    <path d='M16 40 C16 40 0 24 0 16 C0 7.16 7.16 0 16 0 C24.84 0 32 7.16 32 16 C32 24 16 40 16 40Z' fill='${bodyColor}' stroke='white' stroke-width='2'/>
    <circle cx='16' cy='15' r='9' fill='white' opacity='0.9'/>
    <text x='16' y='19' text-anchor='middle' font-size='12' font-weight='700' font-family='Arial' fill='${bodyColor}'>${letter}</text>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatDeadline(deadline) {
  if (!deadline) return '';
  const d = new Date(deadline);
  const now = new Date();
  const diffMs = d - now;
  const minsLeft = Math.floor(diffMs / 60000);
  if (minsLeft < 0) {
    const overdue = Math.abs(minsLeft);
    if (overdue < 60) return `${overdue}m overdue`;
    if (overdue < 1440) return `${Math.floor(overdue / 60)}h overdue`;
    return `${Math.floor(overdue / 1440)}d overdue`;
  }
  if (minsLeft < 60) return `${minsLeft}m left`;
  if (minsLeft < 1440) return `${Math.floor(minsLeft / 60)}h left`;
  return `${Math.floor(minsLeft / 1440)}d left`;
}

function findNearestItem(lat, lng, segments, devices, maxDistance = 50) {
  let nearest = null;
  let minDist = maxDistance;

  if (segments) {
    for (const seg of segments) {
      const sLat = parseFloat(seg.gps_start_lat);
      const sLng = parseFloat(seg.gps_start_lng);
      const eLat = parseFloat(seg.gps_end_lat);
      const eLng = parseFloat(seg.gps_end_lng);
      if (isNaN(sLat)) continue;
      const d1 = calculateDistance(lat, lng, sLat, sLng);
      const d2 = !isNaN(eLat) ? calculateDistance(lat, lng, eLat, eLng) : d1;
      const d = Math.min(d1, d2);
      if (d < minDist) {
        minDist = d;
        nearest = { type: 'segment', id: seg.segment_id, distance: Math.round(d) };
      }
    }
  }

  if (devices) {
    for (const dev of devices) {
      const dLat = parseFloat(dev.gps_lat);
      const dLng = parseFloat(dev.gps_lng);
      if (isNaN(dLat)) continue;
      const d = calculateDistance(lat, lng, dLat, dLng);
      if (d < minDist) {
        minDist = d;
        nearest = { type: 'device', id: dev.device_id, distance: Math.round(d) };
      }
    }
  }

  return nearest;
}

// Touch-friendly button style
const btnBase = (bg, minW) => ({
  padding: '10px 14px', background: bg, color: '#fff', border: 'none',
  borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
  minHeight: 44, minWidth: minW || 44, display: 'inline-flex', alignItems: 'center',
  justifyContent: 'center', gap: 4,
});

// Small preset thumbnail for the style picker grid
function PresetThumb({ preset, selected, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        width: 36, height: 36, borderRadius: 6, cursor: 'pointer',
        border: selected ? '2px solid #1976D2' : '2px solid #ddd',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: preset.color,
        position: 'relative',
      }}
      title={preset.label}
    >
      {preset.animated && (
        <div style={{
          position: 'absolute', top: -3, right: -3, width: 8, height: 8,
          borderRadius: '50%', background: '#FF5722', border: '1px solid #fff',
        }} />
      )}
      <span style={{ color: '#fff', fontSize: 11, fontWeight: 700, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
        {preset.letter}
      </span>
    </div>
  );
}

export default function MapNotesLayer({
  notes, darkMode, loggedInUser, projectId, section, onNotesChanged,
  isAddingNote, onAddNoteToggle, segments, devices, isAdmin, isMobile,
  mapZoom,
}) {
  const [selectedNote, setSelectedNote] = useState(null);
  const [newNotePos, setNewNotePos] = useState(null);
  const [newNoteText, setNewNoteText] = useState('');
  const [newNoteCategory, setNewNoteCategory] = useState('general');
  const [newNotePriority, setNewNotePriority] = useState('normal');
  const [newNoteCrewType, setNewNoteCrewType] = useState('');
  const [newNoteAssignEmail, setNewNoteAssignEmail] = useState('');
  const [newNoteAssignName, setNewNoteAssignName] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const createPhotoRef = useRef(null);
  const existingPhotoRef = useRef(null);

  // New note creation fields
  const [newNotePinStyle, setNewNotePinStyle] = useState('');
  const [newNoteVisibility, setNewNoteVisibility] = useState('shared');
  const [newNoteDeadline, setNewNoteDeadline] = useState('');
  const [newNoteContractor, setNewNoteContractor] = useState('');
  const [showStylePicker, setShowStylePicker] = useState(false);
  const [customColor, setCustomColor] = useState('#FF5722');

  // Popup action states
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignEmail, setAssignEmail] = useState('');
  const [assignName, setAssignName] = useState('');
  const [assignCrewType, setAssignCrewType] = useState('');
  const [showCompleteForm, setShowCompleteForm] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [completionPhotos, setCompletionPhotos] = useState([]);
  const completionPhotoRef = useRef(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Edit mode state (admin)
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [editCategory, setEditCategory] = useState('general');
  const [editPriority, setEditPriority] = useState('normal');
  const [editPinStyle, setEditPinStyle] = useState('');
  const [editVisibility, setEditVisibility] = useState('shared');
  const [editDeadline, setEditDeadline] = useState('');
  const [editCrewType, setEditCrewType] = useState('');
  const [editContractor, setEditContractor] = useState('');
  const [showEditStylePicker, setShowEditStylePicker] = useState(false);
  const [editCustomColor, setEditCustomColor] = useState('#FF5722');
  const [editSaving, setEditSaving] = useState(false);

  // Pin move mode — must be explicitly toggled before any pin becomes draggable
  const [pinMoveMode, setPinMoveMode] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [undoLoading, setUndoLoading] = useState(false);

  // Thread state
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [replySaving, setReplySaving] = useState(false);
  const [readReceipts, setReadReceipts] = useState([]);
  const [noteActivity, setNoteActivity] = useState([]);
  const [showActivity, setShowActivity] = useState(false);

  // Inject CSS keyframes on mount
  useEffect(() => {
    if (document.getElementById('lyt-pin-animations')) return;
    const style = document.createElement('style');
    style.id = 'lyt-pin-animations';
    style.textContent = getAllKeyframes();
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById('lyt-pin-animations');
      if (el) el.remove();
    };
  }, []);

  // Load thread data when a note is selected
  useEffect(() => {
    if (!selectedNote) {
      setReplies([]);
      setReadReceipts([]);
      setNoteActivity([]);
      setShowActivity(false);
      return;
    }
    const noteId = selectedNote.note_id;
    loadNoteReplies(noteId).then(r => setReplies(r || []));
    loadNoteReads(noteId).then(r => setReadReceipts(r || []));
    loadNoteActivity(noteId).then(r => setNoteActivity(r || []));
    // Auto mark-read
    if (loggedInUser?.email) {
      markNoteRead(noteId, loggedInUser.email, loggedInUser.name || loggedInUser.company || '');
    }
  }, [selectedNote, loggedInUser]);

  // Determine which animated pins to show (smart: zoom >= 16, max 30)
  const animatedNoteIds = useMemo(() => {
    if (!notes) return new Set();
    const animatedNotes = notes.filter(n => {
      const ps = n.pin_style;
      if (!ps) return false;
      const preset = getPreset(ps);
      return preset && preset.animated;
    });
    if (animatedNotes.length > 30) return new Set(); // too many, go static
    return new Set(animatedNotes.map(n => n.note_id));
  }, [notes, mapZoom]);

  // Client-side visibility filter
  const visibleNotes = useMemo(() => {
    if (!notes) return [];
    if (isAdmin) return notes;
    const email = loggedInUser?.email || '';
    const role = loggedInUser?.role || '';
    const company = loggedInUser?.company || '';
    return notes.filter(note => {
      const vis = note.visibility || 'shared';
      if (vis === 'shared') return true;
      if (note.author_email === email) return true;
      if (vis === 'inhouse' && (role === 'employee' || role === 'inhouse')) return true;
      if (vis === 'admin') return false;
      // contractor-specific: visibility value = company name
      if (company && vis === company) return true;
      return false;
    });
  }, [notes, isAdmin, loggedInUser]);

  const handleMapClick = useCallback((e) => {
    if (!isAddingNote) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setNewNotePos({ lat, lng });
    setNewNoteText('');
    setNewNoteCategory('general');
    setNewNotePriority('normal');
    setNewNoteCrewType('');
    setNewNoteAssignEmail('');
    setNewNoteAssignName('');
    setPendingPhotos([]);
    setNewNotePinStyle('');
    setNewNoteVisibility('shared');
    setNewNoteDeadline('');
    setNewNoteContractor('');
    setShowStylePicker(false);
  }, [isAddingNote]);

  MapNotesLayer.handleMapClick = handleMapClick;

  const handleSaveNote = useCallback(async () => {
    if (!newNotePos || !newNoteText.trim()) return;
    setSaving(true);
    try {
      const { createMapNote } = await import('../../services/mapService');
      const nearestItem = findNearestItem(newNotePos.lat, newNotePos.lng, segments, devices);

      const category = newNoteCategory;
      const priority = category === 'safety' ? 'critical' : newNotePriority;
      const status = newNoteAssignEmail ? 'assigned' : 'open';

      // Resolve visibility value
      let vis = newNoteVisibility;
      if (vis === 'contractor' && newNoteContractor) vis = newNoteContractor;

      const result = await createMapNote({
        project_id: projectId,
        section: section || null,
        lat: newNotePos.lat,
        lng: newNotePos.lng,
        segment_id: nearestItem?.type === 'segment' ? nearestItem.id : null,
        device_id: nearestItem?.type === 'device' ? nearestItem.id : null,
        text: newNoteText.trim(),
        category,
        priority,
        status,
        target_crew_type: newNoteCrewType || null,
        assigned_to_email: newNoteAssignEmail || null,
        assigned_to_name: newNoteAssignName || null,
        author_email: loggedInUser?.email || '',
        author_name: loggedInUser?.name || loggedInUser?.company || '',
        author_role: loggedInUser?.role || '',
        pin_style: newNotePinStyle || null,
        visibility: vis,
        deadline: newNoteDeadline ? new Date(newNoteDeadline).toISOString() : null,
      });

      if (result.success) {
        if (pendingPhotos.length > 0 && result.data?.note_id) {
          for (const file of pendingPhotos) {
            await uploadNotePhoto(result.data.note_id, file);
          }
          setPendingPhotos([]);
        }
        if (category === 'safety') {
          import('../../services/notificationService').then(ns => {
            ns.notifySafetyConcern(
              newNoteText.trim(),
              loggedInUser?.name || loggedInUser?.email || '',
              projectId,
              newNotePos.lat,
              newNotePos.lng
            );
          });
        }
        if (newNoteAssignEmail) {
          import('../../services/notificationService').then(ns => {
            ns.notifyNoteAssigned(
              newNoteAssignEmail,
              newNoteText.trim(),
              loggedInUser?.name || loggedInUser?.email || '',
              projectId,
              newNoteCrewType || null
            );
          });
        }
        setNewNotePos(null);
        onAddNoteToggle(false);
        if (onNotesChanged) onNotesChanged();
      }
    } catch (err) {
      console.error('[MapNotesLayer] Save failed:', err);
    } finally {
      setSaving(false);
    }
  }, [newNotePos, newNoteText, newNoteCategory, newNotePriority, newNoteCrewType,
      newNoteAssignEmail, newNoteAssignName, projectId, section, loggedInUser,
      segments, devices, onAddNoteToggle, onNotesChanged, pendingPhotos,
      newNotePinStyle, newNoteVisibility, newNoteDeadline, newNoteContractor]);

  // Status transition handler
  const handleTransition = useCallback(async (note, toStatus, extraParams = {}) => {
    setActionLoading(true);
    try {
      const { transitionMapNote } = await import('../../services/mapService');

      // Upload completion photos if any
      if (toStatus === 'complete' && completionPhotos.length > 0) {
        for (const file of completionPhotos) {
          await uploadNotePhoto(note.note_id, file);
        }
        setCompletionPhotos([]);
      }

      const result = await transitionMapNote(note.note_id, toStatus, {
        actor_email: loggedInUser?.email || '',
        actor_name: loggedInUser?.name || loggedInUser?.company || '',
        ...extraParams,
      });
      if (result.success) {
        if (toStatus === 'assigned' && extraParams.assigned_to_email) {
          import('../../services/notificationService').then(ns => {
            ns.notifyNoteAssigned(
              extraParams.assigned_to_email,
              note.text,
              loggedInUser?.name || loggedInUser?.email || '',
              projectId,
              extraParams.target_crew_type || null
            );
          });
        }
        if (toStatus === 'complete') {
          import('../../services/notificationService').then(ns => {
            ns.notifyNoteCompleteReported(
              note.text,
              loggedInUser?.name || loggedInUser?.email || '',
              extraParams.completion_notes || ''
            );
          });
        }
        setSelectedNote(null);
        setShowAssignForm(false);
        setShowCompleteForm(false);
        if (onNotesChanged) onNotesChanged();
      }
    } catch (err) {
      console.error('[MapNotesLayer] Transition failed:', err);
    } finally {
      setActionLoading(false);
    }
  }, [loggedInUser, projectId, onNotesChanged, completionPhotos]);

  // Reply handler
  const handleSendReply = useCallback(async () => {
    if (!selectedNote || !replyText.trim()) return;
    setReplySaving(true);
    try {
      const result = await createNoteReply({
        note_id: selectedNote.note_id,
        text: replyText.trim(),
        author_email: loggedInUser?.email || '',
        author_name: loggedInUser?.name || loggedInUser?.company || '',
        author_role: loggedInUser?.role || '',
      });
      if (result.success) {
        setReplies(prev => [...prev, result.data]);
        setReplyText('');
      }
    } catch (err) {
      console.error('[MapNotesLayer] Reply failed:', err);
    } finally {
      setReplySaving(false);
    }
  }, [selectedNote, replyText, loggedInUser]);

  // Start editing a note (populate fields from current note)
  const startEditing = useCallback((note) => {
    setEditText(note.text || '');
    setEditCategory(note.category || 'general');
    setEditPriority(note.priority || 'normal');
    setEditPinStyle(note.pin_style || '');
    setEditVisibility(note.visibility || 'shared');
    setEditDeadline(note.deadline ? new Date(note.deadline).toISOString().slice(0, 16) : '');
    setEditCrewType(note.target_crew_type || '');
    setEditContractor('');
    setShowEditStylePicker(false);
    if (note.pin_style && isCustomStyle(note.pin_style)) {
      setEditCustomColor(getCustomColor(note.pin_style) || '#FF5722');
    }
    setIsEditing(true);
  }, []);

  // Save edited note
  const handleSaveEdit = useCallback(async () => {
    if (!selectedNote || !editText.trim()) return;
    setEditSaving(true);
    try {
      let vis = editVisibility;
      if (vis === 'contractor' && editContractor) vis = editContractor;

      const fields = {
        text: editText.trim(),
        category: editCategory,
        priority: editCategory === 'safety' ? 'critical' : editPriority,
        pin_style: editPinStyle || null,
        visibility: vis,
        deadline: editDeadline ? new Date(editDeadline).toISOString() : null,
        target_crew_type: editCrewType || null,
      };

      const result = await editMapNote(
        selectedNote.note_id,
        fields,
        loggedInUser?.email || '',
        loggedInUser?.name || loggedInUser?.company || '',
      );
      if (result.success) {
        setIsEditing(false);
        setSelectedNote(null);
        if (onNotesChanged) onNotesChanged();
      }
    } catch (err) {
      console.error('[MapNotesLayer] Edit failed:', err);
    } finally {
      setEditSaving(false);
    }
  }, [selectedNote, editText, editCategory, editPriority, editPinStyle,
      editVisibility, editDeadline, editCrewType, editContractor, loggedInUser, onNotesChanged]);

  // Upload photo to an existing note
  const handleExistingNotePhoto = useCallback(async (e) => {
    if (!selectedNote || !e.target.files?.length) return;
    setPhotoUploading(true);
    try {
      for (const file of e.target.files) {
        await uploadNotePhoto(selectedNote.note_id, file);
      }
      if (onNotesChanged) onNotesChanged();
    } catch (err) {
      console.error('[MapNotesLayer] Photo upload failed:', err);
    } finally {
      setPhotoUploading(false);
      if (existingPhotoRef.current) existingPhotoRef.current.value = '';
    }
  }, [selectedNote, onNotesChanged]);

  // Remove photo from a note
  const handleRemovePhoto = useCallback(async (note, photoUrl) => {
    setPhotoUploading(true);
    try {
      await deleteNotePhoto(note.note_id, photoUrl);
      if (onNotesChanged) onNotesChanged();
    } catch (err) {
      console.error('[MapNotesLayer] Photo delete failed:', err);
    } finally {
      setPhotoUploading(false);
    }
  }, [onNotesChanged]);

  // Pin drag handler — auto-exits move mode after drag
  const handleDragEnd = useCallback(async (note, e) => {
    const newLat = e.latLng.lat();
    const newLng = e.latLng.lng();
    setPinMoveMode(false);
    try {
      const { moveMapNote } = await import('../../services/mapService');
      await moveMapNote(note.note_id, newLat, newLng, loggedInUser?.email);
      if (onNotesChanged) onNotesChanged();
    } catch (err) {
      console.error('[MapNotesLayer] Move failed:', err);
      if (onNotesChanged) onNotesChanged();
    }
  }, [onNotesChanged, loggedInUser]);

  const canDrag = useCallback((note) => {
    if (!pinMoveMode) return false;
    if (isAdmin) return true;
    return note.author_email === loggedInUser?.email;
  }, [pinMoveMode, isAdmin, loggedInUser]);

  // Build icon cache keyed by status+category+priority+pinStyle+deadline
  const noteIcons = useMemo(() => {
    const icons = {};
    if (!visibleNotes) return icons;
    for (const note of visibleNotes) {
      if (animatedNoteIds.has(note.note_id)) continue; // animated pins use OverlayView
      const st = note.status || 'open';
      const cat = note.category || 'general';
      const pri = note.priority || 'normal';
      const ps = note.pin_style || '';
      const dl = note.deadline || '';
      const key = `${st}-${cat}-${pri}-${ps}-${dl ? 'dl' : ''}`;
      if (!icons[key]) {
        icons[key] = {
          url: buildPinSvg(st, cat, pri, ps || undefined, dl || undefined),
          scaledSize: window.google ? new window.google.maps.Size(32, 40) : undefined,
          anchor: window.google ? new window.google.maps.Point(16, 40) : undefined,
        };
      }
    }
    return icons;
  }, [visibleNotes, animatedNoteIds]);

  const isFieldWorker = !isAdmin;
  const userEmail = loggedInUser?.email || '';

  return (
    <>
      {/* Existing note markers — static pins */}
      {visibleNotes && visibleNotes.map(note => {
        if (animatedNoteIds.has(note.note_id)) return null; // rendered via OverlayView
        const st = note.status || 'open';
        const cat = note.category || 'general';
        const pri = note.priority || 'normal';
        const ps = note.pin_style || '';
        const dl = note.deadline || '';
        const key = `${st}-${cat}-${pri}-${ps}-${dl ? 'dl' : ''}`;
        const isVerified = st === 'verified';
        const isDraggable = canDrag(note);

        return (
          <Marker
            key={note.note_id}
            position={{ lat: parseFloat(note.lat), lng: parseFloat(note.lng) }}
            title={note.text.slice(0, 60)}
            icon={noteIcons[key]}
            onClick={() => {
              setSelectedNote(note);
              setShowAssignForm(false);
              setShowCompleteForm(false);
              setIsEditing(false);
              setPinMoveMode(false);
              setConfirmDelete(false);
              setAssignEmail('');
              setAssignName('');
              setAssignCrewType('');
              setCompletionNotes('');
              setCompletionPhotos([]);
              setReplyText('');
            }}
            draggable={isDraggable}
            onDragEnd={isDraggable ? (e) => handleDragEnd(note, e) : undefined}
            zIndex={isVerified ? 100 : (pri === 'critical' ? 1000 : pri === 'high' ? 900 : 800)}
            opacity={isVerified ? 0.4 : 1}
          />
        );
      })}

      {/* Animated pins via OverlayView */}
      {visibleNotes && visibleNotes.map(note => {
        if (!animatedNoteIds.has(note.note_id)) return null;
        const preset = getPreset(note.pin_style);
        if (!preset) return null;
        const cat = note.category || 'general';
        const html = buildAnimatedPinHtml(preset, cat, note.deadline, note.note_id);
        if (!html) return null;

        return (
          <OverlayView
            key={`anim-${note.note_id}`}
            position={{ lat: parseFloat(note.lat), lng: parseFloat(note.lng) }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={() => ({ x: -16, y: -40 })}
          >
            <div
              onClick={() => {
                setSelectedNote(note);
                setShowAssignForm(false);
                setShowCompleteForm(false);
                setIsEditing(false);
                setReplyText('');
              }}
              style={{ cursor: 'pointer' }}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </OverlayView>
        );
      })}

      {/* Selected note popup */}
      {selectedNote && (() => {
        const closeNote = () => { setSelectedNote(null); setShowAssignForm(false); setShowCompleteForm(false); setIsEditing(false); };
        const popupInner = (
          <div style={{ fontFamily: 'system-ui, sans-serif', fontSize: isMobile ? 14 : 13, color: '#333' }}>
            {/* Badges row + Edit button — paddingRight avoids overlap with sheet X button */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 5, marginBottom: 8, paddingRight: isMobile ? 40 : 0 }}>
              <span style={{
                background: STATUS_COLORS[selectedNote.status] || '#607D8B',
                color: '#fff', fontSize: 10, fontWeight: 600, padding: '3px 7px', borderRadius: 3,
              }}>
                {CATEGORY_LABELS[selectedNote.category] || 'General'}
              </span>
              <span style={{
                background: STATUS_COLORS[selectedNote.status] || '#607D8B',
                color: '#fff', fontSize: 10, fontWeight: 600, padding: '3px 7px', borderRadius: 3,
                opacity: 0.85,
              }}>
                {STATUS_LABELS[selectedNote.status] || 'Open'}
              </span>
              {selectedNote.priority && selectedNote.priority !== 'normal' && (
                <span style={{
                  background: PRIORITY_COLORS[selectedNote.priority],
                  color: '#fff', fontSize: 10, fontWeight: 600, padding: '3px 7px', borderRadius: 3,
                }}>
                  {selectedNote.priority.toUpperCase()}
                </span>
              )}
              {selectedNote.pin_style && (
                <span style={{
                  background: '#607D8B', color: '#fff', fontSize: 10, fontWeight: 600,
                  padding: '3px 7px', borderRadius: 3,
                }}>
                  {(getPreset(selectedNote.pin_style)?.label) || selectedNote.pin_style}
                </span>
              )}
              {selectedNote.visibility && selectedNote.visibility !== 'shared' && (
                <span style={{
                  background: '#9C27B0', color: '#fff', fontSize: 10, fontWeight: 600,
                  padding: '3px 7px', borderRadius: 3,
                }}>
                  {selectedNote.visibility === 'admin' ? 'Admin Only' :
                   selectedNote.visibility === 'inhouse' ? 'In-House' :
                   selectedNote.visibility}
                </span>
              )}
              {isAdmin && selectedNote.status !== 'verified' && !isEditing && (
                <button
                  onClick={() => startEditing(selectedNote)}
                  style={{
                    marginLeft: 'auto', background: 'none', border: '1px solid #ccc',
                    borderRadius: 3, padding: '2px 8px', fontSize: 10, cursor: 'pointer',
                    color: '#666', fontWeight: 600,
                  }}
                >
                  Edit
                </button>
              )}
            </div>

            {/* === EDIT MODE === */}
            {isEditing ? (
              <div style={{ marginBottom: 8 }}>
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  style={{ width: '100%', minHeight: 60, padding: 8, border: '1px solid #ccc', borderRadius: 4, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', marginBottom: 6 }}
                />
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <select value={editCategory} onChange={(e) => {
                    setEditCategory(e.target.value);
                    if (e.target.value === 'safety') setEditPriority('critical');
                  }}
                    style={{ flex: 1, padding: 6, fontSize: 12, border: '1px solid #ccc', borderRadius: 4, minHeight: 36 }}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
                  </select>
                  <select value={editCategory === 'safety' ? 'critical' : editPriority} onChange={(e) => setEditPriority(e.target.value)}
                    disabled={editCategory === 'safety'}
                    style={{ flex: 1, padding: 6, fontSize: 12, border: '1px solid #ccc', borderRadius: 4, minHeight: 36, opacity: editCategory === 'safety' ? 0.6 : 1 }}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
                <select value={editCrewType} onChange={(e) => setEditCrewType(e.target.value)}
                  style={{ width: '100%', padding: 6, fontSize: 12, border: '1px solid #ccc', borderRadius: 4, marginBottom: 6, minHeight: 36, boxSizing: 'border-box' }}>
                  <option value="">Crew type (optional)</option>
                  {CREW_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>

                {/* Pin style picker */}
                <div style={{ marginBottom: 6 }}>
                  <button
                    type="button"
                    onClick={() => setShowEditStylePicker(!showEditStylePicker)}
                    style={{
                      background: 'none', border: '1px solid #ccc', borderRadius: 4,
                      padding: '6px 10px', fontSize: 12, cursor: 'pointer', color: '#555',
                      display: 'flex', alignItems: 'center', gap: 6, width: '100%', boxSizing: 'border-box',
                    }}
                  >
                    {editPinStyle ? (
                      <>
                        <span style={{
                          width: 16, height: 16, borderRadius: 3, display: 'inline-block',
                          background: isCustomStyle(editPinStyle)
                            ? getCustomColor(editPinStyle)
                            : (getPreset(editPinStyle)?.color || '#607D8B'),
                        }} />
                        {isCustomStyle(editPinStyle) ? 'Custom color' : (getPreset(editPinStyle)?.label || editPinStyle)}
                      </>
                    ) : 'Pin style (optional)'}
                    <span style={{ marginLeft: 'auto', fontSize: 10 }}>{showEditStylePicker ? '\u25B2' : '\u25BC'}</span>
                  </button>
                  {showEditStylePicker && (
                    <div style={{ marginTop: 4, padding: 8, background: '#f9f9f9', borderRadius: 6, border: '1px solid #eee' }}>
                      {editPinStyle && (
                        <button
                          onClick={() => setEditPinStyle('')}
                          style={{ fontSize: 11, color: '#999', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 6, textDecoration: 'underline' }}
                        >
                          Clear style (use default)
                        </button>
                      )}
                      {PIN_STYLE_GROUPS.map(group => (
                        <div key={group.key} style={{ marginBottom: 6 }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: '#888', marginBottom: 4, textTransform: 'uppercase' }}>
                            {group.label}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 36px)', gap: 4 }}>
                            {PIN_PRESETS.filter(p => p.group === group.key).map(p => (
                              <PresetThumb
                                key={p.key}
                                preset={p}
                                selected={editPinStyle === p.key}
                                onClick={() => setEditPinStyle(p.key)}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                      <div style={{ marginTop: 4 }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#888', marginBottom: 4, textTransform: 'uppercase' }}>Custom</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="color"
                            value={editCustomColor}
                            onChange={(e) => {
                              setEditCustomColor(e.target.value);
                              setEditPinStyle(`custom:${e.target.value}`);
                            }}
                            style={{ width: 36, height: 36, border: 'none', cursor: 'pointer', padding: 0 }}
                          />
                          <span style={{ fontSize: 11, color: '#666' }}>Pick a color</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Visibility */}
                <select
                  value={editVisibility}
                  onChange={(e) => setEditVisibility(e.target.value)}
                  style={{ width: '100%', padding: 6, fontSize: 12, border: '1px solid #ccc', borderRadius: 4, marginBottom: 4, minHeight: 36, boxSizing: 'border-box' }}
                >
                  {VISIBILITY_OPTIONS.map(v => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
                {editVisibility === 'contractor' && (
                  <input
                    placeholder="Contractor company name"
                    value={editContractor}
                    onChange={(e) => setEditContractor(e.target.value)}
                    style={{ width: '100%', padding: 6, fontSize: 12, border: '1px solid #ccc', borderRadius: 4, marginBottom: 4, minHeight: 36, boxSizing: 'border-box' }}
                  />
                )}

                {/* Deadline */}
                <input
                  type="datetime-local"
                  value={editDeadline}
                  onChange={(e) => setEditDeadline(e.target.value)}
                  style={{ width: '100%', padding: 6, fontSize: 12, border: '1px solid #ccc', borderRadius: 4, marginBottom: 4, minHeight: 36, boxSizing: 'border-box', color: editDeadline ? '#333' : '#999' }}
                />
                {editDeadline && (
                  <button
                    onClick={() => setEditDeadline('')}
                    style={{ fontSize: 10, color: '#999', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 4 }}
                  >
                    Clear deadline
                  </button>
                )}

                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  <button
                    onClick={handleSaveEdit}
                    disabled={editSaving || !editText.trim()}
                    style={btnBase(editSaving ? '#999' : '#2196F3', 80)}
                  >
                    {editSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    style={btnBase('#757575', 60)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
            <>
            {/* Deadline */}
            {selectedNote.deadline && (
              <div style={{
                fontSize: 11, marginBottom: 6, padding: '3px 8px', borderRadius: 4,
                background: new Date(selectedNote.deadline) < new Date() ? '#FFEBEE' : '#E3F2FD',
                color: new Date(selectedNote.deadline) < new Date() ? '#D32F2F' : '#1565C0',
                fontWeight: 600, display: 'inline-block',
              }}>
                Due: {new Date(selectedNote.deadline).toLocaleString()} ({formatDeadline(selectedNote.deadline)})
              </div>
            )}

            {/* Target crew type */}
            {selectedNote.target_crew_type && (
              <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>
                Crew: {selectedNote.target_crew_type}
              </div>
            )}

            {/* Note text */}
            <div style={{ whiteSpace: 'pre-wrap', marginBottom: 8, lineHeight: 1.4, fontSize: 13 }}>
              {selectedNote.text}
            </div>

            {/* Photos */}
            {(() => {
              const photos = selectedNote.photos ? (typeof selectedNote.photos === 'string' ? JSON.parse(selectedNote.photos) : selectedNote.photos) : [];
              const canRemovePhoto = isAdmin || selectedNote.author_email === userEmail;
              return photos.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                  {photos.map((url, i) => (
                    <div key={i} style={{ position: 'relative', width: 48, height: 48 }}>
                      <img
                        src={url}
                        alt={`Attachment ${i + 1}`}
                        onClick={() => window.open(url, '_blank')}
                        style={{
                          width: 48, height: 48, objectFit: 'cover', borderRadius: 4,
                          cursor: 'pointer', border: '1px solid #ddd',
                        }}
                      />
                      {canRemovePhoto && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRemovePhoto(selectedNote, url); }}
                          disabled={photoUploading}
                          style={{
                            position: 'absolute', top: -4, right: -4,
                            width: 18, height: 18, borderRadius: '50%',
                            background: '#F44336', color: '#fff', border: 'none',
                            fontSize: 10, fontWeight: 700, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            lineHeight: 1, padding: 0,
                          }}
                        >
                          X
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Add photo button */}
            {selectedNote.status !== 'verified' && (
              <div style={{ marginBottom: 8 }}>
                <input
                  type="file"
                  ref={existingPhotoRef}
                  accept="image/*"
                  
                  multiple
                  onChange={handleExistingNotePhoto}
                  style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}
                />
                <button
                  onClick={() => existingPhotoRef.current?.click()}
                  disabled={photoUploading}
                  style={{
                    ...btnBase(photoUploading ? '#999' : '#607D8B', 0),
                    fontSize: 12, padding: '6px 10px', minHeight: 32,
                  }}
                >
                  {photoUploading ? 'Uploading...' : '+ Photo'}
                </button>
              </div>
            )}

            {/* Attachment */}
            {(selectedNote.segment_id || selectedNote.device_id) && (
              <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
                Attached to: {selectedNote.segment_id || selectedNote.device_id}
              </div>
            )}

            {/* Assignment info */}
            {selectedNote.assigned_to_name && (
              <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>
                Assigned to: {selectedNote.assigned_to_name} ({selectedNote.assigned_to_email})
                {selectedNote.assigned_at && ` - ${timeAgo(selectedNote.assigned_at)}`}
              </div>
            )}

            {/* Completion info */}
            {selectedNote.completed_by && (
              <div style={{ fontSize: 11, color: '#009688', marginBottom: 4 }}>
                Completed by: {selectedNote.completed_by} {selectedNote.completed_at && `- ${timeAgo(selectedNote.completed_at)}`}
                {selectedNote.completion_notes && <div style={{ marginTop: 2 }}>Notes: {selectedNote.completion_notes}</div>}
              </div>
            )}

            {/* Verified info */}
            {selectedNote.verified_by && (
              <div style={{ fontSize: 11, color: '#4CAF50', marginBottom: 4 }}>
                Verified by: {selectedNote.verified_by} {selectedNote.verified_at && `- ${timeAgo(selectedNote.verified_at)}`}
              </div>
            )}

            {/* Author + time */}
            <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
              {selectedNote.author_name || selectedNote.author_email} &middot; {timeAgo(selectedNote.created_at)}
            </div>

            {/* Read receipts */}
            {readReceipts.length > 0 && (
              <div style={{ fontSize: 10, color: '#999', marginBottom: 8 }}>
                Seen by: {readReceipts.map((r, i) => (
                  <span key={i}>
                    {r.user_name || r.user_email.split('@')[0]}
                    {r.read_at && ` (${new Date(r.read_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })})`}
                    {i < readReceipts.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            )}

            {/* === ACTION BUTTONS by status + role === */}
            {renderActions(selectedNote)}

            {/* Assign form (inline) */}
            {showAssignForm && (
              <div style={{ marginTop: 8, padding: 8, background: '#fff8e1', borderRadius: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Assign Note</div>
                <input
                  placeholder="Name"
                  value={assignName}
                  onChange={e => setAssignName(e.target.value)}
                  style={{ width: '100%', padding: 8, fontSize: 13, border: '1px solid #ccc', borderRadius: 4, marginBottom: 4, boxSizing: 'border-box', minHeight: 40 }}
                />
                <input
                  placeholder="Email"
                  value={assignEmail}
                  onChange={e => setAssignEmail(e.target.value)}
                  style={{ width: '100%', padding: 8, fontSize: 13, border: '1px solid #ccc', borderRadius: 4, marginBottom: 4, boxSizing: 'border-box', minHeight: 40 }}
                />
                <select
                  value={assignCrewType}
                  onChange={e => setAssignCrewType(e.target.value)}
                  style={{ width: '100%', padding: 8, fontSize: 13, border: '1px solid #ccc', borderRadius: 4, marginBottom: 6, boxSizing: 'border-box', minHeight: 40 }}
                >
                  <option value="">Crew type (optional)</option>
                  {CREW_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={() => {
                      if (!assignEmail) return;
                      handleTransition(selectedNote, 'assigned', {
                        assigned_to_email: assignEmail,
                        assigned_to_name: assignName,
                        target_crew_type: assignCrewType || undefined,
                      });
                    }}
                    disabled={actionLoading || !assignEmail}
                    style={btnBase(actionLoading ? '#999' : '#FF9800', 100)}
                  >
                    {actionLoading ? 'Saving...' : 'Assign & Notify'}
                  </button>
                  <button onClick={() => setShowAssignForm(false)} style={btnBase('#757575', 60)}>Cancel</button>
                </div>
              </div>
            )}

            {/* Complete form with resolution photo prompt */}
            {showCompleteForm && (
              <div style={{ marginTop: 8, padding: 8, background: '#e0f2f1', borderRadius: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Report Complete</div>
                <textarea
                  placeholder="Completion notes (optional)"
                  value={completionNotes}
                  onChange={e => setCompletionNotes(e.target.value)}
                  style={{ width: '100%', minHeight: 50, padding: 8, fontSize: 13, border: '1px solid #ccc', borderRadius: 4, marginBottom: 6, boxSizing: 'border-box', resize: 'vertical' }}
                />
                {/* Resolution photo prompt */}
                <div style={{ marginBottom: 6, padding: 6, background: '#fff', borderRadius: 4, border: '1px dashed #009688' }}>
                  <div style={{ fontSize: 11, color: '#00695C', marginBottom: 4, fontWeight: 600 }}>
                    Attach photo of completed work (recommended)
                  </div>
                  <input
                    type="file"
                    ref={completionPhotoRef}
                    accept="image/*"
                    
                    multiple
                    onChange={(e) => {
                      if (e.target.files?.length) {
                        setCompletionPhotos(prev => [...prev, ...Array.from(e.target.files)]);
                      }
                    }}
                    style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}
                  />
                  <button
                    type="button"
                    onClick={() => completionPhotoRef.current?.click()}
                    style={{ ...btnBase('#009688', 0), fontSize: 12, padding: '6px 10px', minHeight: 32 }}
                  >
                    + Completion Photo
                  </button>
                  {completionPhotos.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                      {completionPhotos.map((file, i) => (
                        <div key={i} style={{ position: 'relative', width: 40, height: 40 }}>
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
                          />
                          <button
                            onClick={() => setCompletionPhotos(prev => prev.filter((_, idx) => idx !== i))}
                            style={{
                              position: 'absolute', top: -4, right: -4,
                              width: 16, height: 16, borderRadius: '50%',
                              background: '#F44336', color: '#fff', border: 'none',
                              fontSize: 9, fontWeight: 700, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              lineHeight: 1, padding: 0,
                            }}
                          >
                            X
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={() => handleTransition(selectedNote, 'complete', { completion_notes: completionNotes })}
                    disabled={actionLoading}
                    style={btnBase(actionLoading ? '#999' : '#009688', 100)}
                  >
                    {actionLoading ? 'Saving...' : 'Submit'}
                  </button>
                  <button onClick={() => { setShowCompleteForm(false); setCompletionPhotos([]); }} style={btnBase('#757575', 60)}>Cancel</button>
                </div>
              </div>
            )}

            {/* === THREAD / REPLIES === */}
            {replies.length > 0 && (
              <div style={{ marginTop: 10, borderTop: '1px solid #eee', paddingTop: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#555' }}>
                  Replies ({replies.length})
                </div>
                <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                  {replies.map(r => (
                    <div key={r.reply_id} style={{ marginBottom: 6, padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                      <div style={{ fontSize: 11, color: '#1565C0', fontWeight: 600 }}>
                        {r.author_name || r.author_email}
                        <span style={{ fontWeight: 400, color: '#999', marginLeft: 6 }}>
                          {timeAgo(r.created_at)}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: '#333', whiteSpace: 'pre-wrap', marginTop: 2 }}>
                        {r.text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reply input */}
            {selectedNote.status !== 'verified' && (
              <div style={{ marginTop: 8, display: 'flex', gap: 4 }}>
                <input
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  placeholder="Reply..."
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendReply(); } }}
                  style={{
                    flex: 1, padding: '8px 10px', fontSize: 12, border: '1px solid #ccc',
                    borderRadius: 4, boxSizing: 'border-box', minHeight: 36,
                  }}
                />
                <button
                  onClick={handleSendReply}
                  disabled={replySaving || !replyText.trim()}
                  style={{
                    ...btnBase(replySaving ? '#999' : '#1565C0', 50),
                    fontSize: 12, padding: '6px 10px', minHeight: 36,
                  }}
                >
                  {replySaving ? '...' : 'Send'}
                </button>
              </div>
            )}

            {/* Activity log (collapsible) */}
            {noteActivity.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <button
                  onClick={() => setShowActivity(!showActivity)}
                  style={{
                    background: 'none', border: 'none', color: '#999', fontSize: 11,
                    cursor: 'pointer', padding: '4px 0', textDecoration: 'underline',
                  }}
                >
                  {showActivity ? 'Hide activity' : `Activity (${noteActivity.length})`}
                </button>
                {showActivity && (
                  <div style={{ maxHeight: 120, overflowY: 'auto', marginTop: 4 }}>
                    {noteActivity.map((a, i) => (
                      <div key={i} style={{ fontSize: 10, color: '#888', padding: '2px 0' }}>
                        <span style={{ color: '#555' }}>
                          {a.event_type === 'created' ? 'Created' :
                           a.event_type === 'reply' ? 'Replied' :
                           a.event_type === 'status_change' ? 'Status changed' :
                           a.event_type === 'viewed' ? 'Viewed' :
                           a.event_type === 'photo_added' ? 'Photo added' :
                           a.event_type === 'moved' ? 'Moved' : a.event_type}
                        </span>
                        {' by '}
                        {a.actor_name || a.actor_email || 'system'}
                        {' - '}
                        {timeAgo(a.created_at)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pin actions — move & delete (admin or author, not verified) */}
            {(isAdmin || selectedNote.author_email === loggedInUser?.email) && selectedNote.status !== 'verified' && (
              <div style={{ marginTop: 6, textAlign: 'center' }}>
                {pinMoveMode ? (
                  <>
                    <div style={{ fontSize: 10, color: '#f57c00', fontWeight: 600, marginBottom: 4 }}>
                      {isMobile ? 'Tap & hold pin to move' : 'Drag pin to reposition'}
                    </div>
                    <button
                      onClick={() => setPinMoveMode(false)}
                      style={{ fontSize: 11, padding: '3px 10px', background: '#f57c00', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                    >
                      Done Moving
                    </button>
                  </>
                ) : confirmDelete ? (
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: '#e53935', fontWeight: 600 }}>Delete this pin?</span>
                    <button
                      disabled={deleteLoading}
                      onClick={async () => {
                        setDeleteLoading(true);
                        try {
                          const { deleteMapNote } = await import('../../services/mapService');
                          await deleteMapNote(selectedNote.note_id, loggedInUser?.email);
                          setSelectedNote(null);
                          setConfirmDelete(false);
                          if (onNotesChanged) onNotesChanged();
                        } catch (err) {
                          console.error('[MapNotesLayer] Delete failed:', err);
                        } finally {
                          setDeleteLoading(false);
                        }
                      }}
                      style={{ fontSize: 11, padding: '3px 10px', background: '#e53935', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: deleteLoading ? 0.6 : 1 }}
                    >
                      {deleteLoading ? 'Deleting...' : 'Yes, Delete'}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(false)}
                      style={{ fontSize: 11, padding: '3px 10px', background: 'transparent', color: '#999', border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => { setPinMoveMode(true); setSelectedNote(null); }}
                      style={{ fontSize: 11, padding: '3px 10px', background: 'transparent', color: '#999', border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer' }}
                    >
                      Move Pin
                    </button>
                    {isAdmin && selectedNote.move_history && selectedNote.move_history.length > 0 && (
                      <button
                        disabled={undoLoading}
                        onClick={async () => {
                          setUndoLoading(true);
                          try {
                            const { undoMoveMapNote } = await import('../../services/mapService');
                            const result = await undoMoveMapNote(selectedNote.note_id, loggedInUser?.email);
                            if (result.success) {
                              setSelectedNote(null);
                              if (onNotesChanged) onNotesChanged();
                            }
                          } catch (err) {
                            console.error('[MapNotesLayer] Undo move failed:', err);
                          } finally {
                            setUndoLoading(false);
                          }
                        }}
                        style={{ fontSize: 11, padding: '3px 10px', background: 'transparent', color: '#1565C0', border: '1px solid #1565C044', borderRadius: 4, cursor: 'pointer', opacity: undoLoading ? 0.6 : 1 }}
                      >
                        {undoLoading ? 'Undoing...' : 'Undo Move'}
                      </button>
                    )}
                    <button
                      onClick={() => setConfirmDelete(true)}
                      style={{ fontSize: 11, padding: '3px 10px', background: 'transparent', color: '#e53935', border: '1px solid #e5393544', borderRadius: 4, cursor: 'pointer' }}
                    >
                      Delete Pin
                    </button>
                  </div>
                )}
              </div>
            )}
            </>
            )}
          </div>
        );

        if (isMobile) {
          return (
            <>
              <div onClick={closeNote} style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
                background: 'rgba(0,0,0,0.4)',
              }} />
              <div style={{
                position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1001,
                background: '#fff', borderRadius: '16px 16px 0 0',
                boxShadow: '0 -4px 24px rgba(0,0,0,0.15)',
                maxHeight: '75vh',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
                  <div style={{ width: 36, height: 4, borderRadius: 2, background: '#ccc' }} />
                </div>
                <button onClick={closeNote} style={{
                  position: 'absolute', top: 8, right: 12, background: 'none', border: 'none',
                  fontSize: 24, color: '#999', cursor: 'pointer', padding: 6, lineHeight: 1,
                  minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>×</button>
                <div style={{ overflowY: 'auto', maxHeight: 'calc(75vh - 44px)', padding: '0 16px 16px', WebkitOverflowScrolling: 'touch' }}>
                  {popupInner}
                </div>
              </div>
            </>
          );
        }

        return (
          <InfoWindow
            position={{ lat: parseFloat(selectedNote.lat), lng: parseFloat(selectedNote.lng) }}
            onCloseClick={closeNote}
            options={{ maxWidth: 380 }}
          >
            {popupInner}
          </InfoWindow>
        );
      })()}

      {/* New note placement marker + form */}
      {isAddingNote && newNotePos && (
        <>
          <Marker
            position={newNotePos}
            icon={{
              url: buildPinSvg(
                'open', newNoteCategory,
                newNoteCategory === 'safety' ? 'critical' : newNotePriority,
                newNotePinStyle || undefined,
                newNoteDeadline ? newNoteDeadline : undefined
              ),
              scaledSize: window.google ? new window.google.maps.Size(32, 40) : undefined,
              anchor: window.google ? new window.google.maps.Point(16, 40) : undefined,
            }}
            zIndex={2000}
          />
          {(() => {
            const closeForm = () => { setNewNotePos(null); onAddNoteToggle(false); };
            const m = isMobile;
            const formContent = (
            <div style={{ fontFamily: 'system-ui, sans-serif', fontSize: m ? 14 : 13, color: '#333' }}>
              <div style={{ fontWeight: 600, marginBottom: 6, fontSize: m ? 16 : 14 }}>Add Note</div>
              <textarea
                value={newNoteText}
                onChange={(e) => setNewNoteText(e.target.value)}
                placeholder="Type your note..."
                style={{ width: '100%', minHeight: m ? 80 : 60, padding: m ? 10 : 8, border: '1px solid #ccc', borderRadius: 4, fontSize: m ? 16 : 13, resize: 'vertical', boxSizing: 'border-box' }}
                autoFocus
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <select value={newNoteCategory} onChange={(e) => {
                  setNewNoteCategory(e.target.value);
                  if (e.target.value === 'safety') setNewNotePriority('critical');
                }}
                  style={{ flex: 1, padding: 6, fontSize: 12, border: '1px solid #ccc', borderRadius: 4, minHeight: 36 }}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
                </select>
                <select value={newNoteCategory === 'safety' ? 'critical' : newNotePriority} onChange={(e) => setNewNotePriority(e.target.value)}
                  disabled={newNoteCategory === 'safety'}
                  style={{ flex: 1, padding: 6, fontSize: 12, border: '1px solid #ccc', borderRadius: 4, minHeight: 36, opacity: newNoteCategory === 'safety' ? 0.6 : 1 }}>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                </select>
              </div>

              {/* Crew type selector */}
              <select value={newNoteCrewType} onChange={(e) => setNewNoteCrewType(e.target.value)}
                style={{ width: '100%', padding: 6, fontSize: 12, border: '1px solid #ccc', borderRadius: 4, marginTop: 6, minHeight: 36, boxSizing: 'border-box' }}>
                <option value="">Notify crew type (optional)</option>
                {CREW_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>

              {/* === PIN STYLE PICKER === */}
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => setShowStylePicker(!showStylePicker)}
                  style={{
                    background: 'none', border: '1px solid #ccc', borderRadius: 4,
                    padding: '6px 10px', fontSize: 12, cursor: 'pointer', color: '#555',
                    display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  {newNotePinStyle ? (
                    <>
                      <span style={{
                        width: 16, height: 16, borderRadius: 3, display: 'inline-block',
                        background: isCustomStyle(newNotePinStyle)
                          ? getCustomColor(newNotePinStyle)
                          : (getPreset(newNotePinStyle)?.color || '#607D8B'),
                      }} />
                      {isCustomStyle(newNotePinStyle) ? 'Custom color' : (getPreset(newNotePinStyle)?.label || newNotePinStyle)}
                    </>
                  ) : (
                    'Pin style (optional)'
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 10 }}>{showStylePicker ? '\u25B2' : '\u25BC'}</span>
                </button>
                {showStylePicker && (
                  <div style={{ marginTop: 4, padding: 8, background: '#f9f9f9', borderRadius: 6, border: '1px solid #eee' }}>
                    {/* Clear selection */}
                    {newNotePinStyle && (
                      <button
                        onClick={() => setNewNotePinStyle('')}
                        style={{ fontSize: 11, color: '#999', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 6, textDecoration: 'underline' }}
                      >
                        Clear style (use default)
                      </button>
                    )}
                    {PIN_STYLE_GROUPS.map(group => (
                      <div key={group.key} style={{ marginBottom: 6 }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#888', marginBottom: 4, textTransform: 'uppercase' }}>
                          {group.label}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 36px)', gap: 4 }}>
                          {PIN_PRESETS.filter(p => p.group === group.key).map(p => (
                            <PresetThumb
                              key={p.key}
                              preset={p}
                              selected={newNotePinStyle === p.key}
                              onClick={() => setNewNotePinStyle(p.key)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                    {/* Custom color */}
                    <div style={{ marginTop: 4 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#888', marginBottom: 4, textTransform: 'uppercase' }}>Custom</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input
                          type="color"
                          value={customColor}
                          onChange={(e) => {
                            setCustomColor(e.target.value);
                            setNewNotePinStyle(`custom:${e.target.value}`);
                          }}
                          style={{ width: 36, height: 36, border: 'none', cursor: 'pointer', padding: 0 }}
                        />
                        <span style={{ fontSize: 11, color: '#666' }}>Pick a color</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* === VISIBILITY (admin only) === */}
              {isAdmin && (
                <div style={{ marginTop: 6 }}>
                  <select
                    value={newNoteVisibility}
                    onChange={(e) => setNewNoteVisibility(e.target.value)}
                    style={{ width: '100%', padding: 6, fontSize: 12, border: '1px solid #ccc', borderRadius: 4, minHeight: 36, boxSizing: 'border-box' }}
                  >
                    {VISIBILITY_OPTIONS.map(v => (
                      <option key={v.value} value={v.value}>{v.label}</option>
                    ))}
                  </select>
                  {newNoteVisibility === 'contractor' && (
                    <input
                      placeholder="Contractor company name"
                      value={newNoteContractor}
                      onChange={(e) => setNewNoteContractor(e.target.value)}
                      style={{ width: '100%', padding: 6, fontSize: 12, border: '1px solid #ccc', borderRadius: 4, marginTop: 4, minHeight: 36, boxSizing: 'border-box' }}
                    />
                  )}
                </div>
              )}

              {/* === DEADLINE PICKER === */}
              <div style={{ marginTop: 6 }}>
                <input
                  type="datetime-local"
                  value={newNoteDeadline}
                  onChange={(e) => setNewNoteDeadline(e.target.value)}
                  style={{ width: '100%', padding: 6, fontSize: 12, border: '1px solid #ccc', borderRadius: 4, minHeight: 36, boxSizing: 'border-box', color: newNoteDeadline ? '#333' : '#999' }}
                  placeholder="Due by (optional)"
                />
                {newNoteDeadline && (
                  <button
                    onClick={() => setNewNoteDeadline('')}
                    style={{ fontSize: 10, color: '#999', background: 'none', border: 'none', cursor: 'pointer', marginTop: 2 }}
                  >
                    Clear deadline
                  </button>
                )}
              </div>

              {/* Photo attachment */}
              <div style={{ marginTop: 6 }}>
                <input
                  type="file"
                  ref={createPhotoRef}
                  accept="image/*"
                  
                  multiple
                  onChange={(e) => {
                    if (e.target.files?.length) {
                      setPendingPhotos(prev => [...prev, ...Array.from(e.target.files)]);
                    }
                  }}
                  style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}
                />
                <button
                  type="button"
                  onClick={() => createPhotoRef.current?.click()}
                  style={{
                    ...btnBase('#607D8B', 0),
                    fontSize: 12, padding: '6px 10px', minHeight: 32,
                  }}
                >
                  + Photo
                </button>
                {pendingPhotos.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                    {pendingPhotos.map((file, i) => (
                      <div key={i} style={{ position: 'relative', width: 40, height: 40 }}>
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          style={{
                            width: 40, height: 40, objectFit: 'cover', borderRadius: 4,
                            border: '1px solid #ddd',
                          }}
                        />
                        <button
                          onClick={() => setPendingPhotos(prev => prev.filter((_, idx) => idx !== i))}
                          style={{
                            position: 'absolute', top: -4, right: -4,
                            width: 16, height: 16, borderRadius: '50%',
                            background: '#F44336', color: '#fff', border: 'none',
                            fontSize: 9, fontWeight: 700, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            lineHeight: 1, padding: 0,
                          }}
                        >
                          X
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Optional immediate assignment */}
              {isAdmin && (
                <div style={{ marginTop: 6, padding: 6, background: '#f5f5f5', borderRadius: 4 }}>
                  <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>Assign immediately (optional)</div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <input
                      placeholder="Name"
                      value={newNoteAssignName}
                      onChange={e => setNewNoteAssignName(e.target.value)}
                      style={{ flex: 1, padding: 6, fontSize: 12, border: '1px solid #ccc', borderRadius: 4, minHeight: 36 }}
                    />
                    <input
                      placeholder="Email"
                      value={newNoteAssignEmail}
                      onChange={e => setNewNoteAssignEmail(e.target.value)}
                      style={{ flex: 1, padding: 6, fontSize: 12, border: '1px solid #ccc', borderRadius: 4, minHeight: 36 }}
                    />
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button
                  onClick={handleSaveNote}
                  disabled={saving || !newNoteText.trim()}
                  style={{ ...btnBase(saving ? '#999' : '#2196F3', 0), minHeight: m ? 48 : 44 }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={closeForm}
                  style={{ ...btnBase('#757575', 0), minHeight: m ? 48 : 44 }}
                >
                  Cancel
                </button>
              </div>
            </div>
            );

            if (m) {
              return (
                <>
                  <div onClick={closeForm} style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
                    background: 'rgba(0,0,0,0.4)',
                  }} />
                  <div style={{
                    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1001,
                    background: '#fff', borderRadius: '16px 16px 0 0',
                    boxShadow: '0 -4px 24px rgba(0,0,0,0.15)',
                    maxHeight: '80vh',
                    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
                      <div style={{ width: 36, height: 4, borderRadius: 2, background: '#ccc' }} />
                    </div>
                    <button onClick={closeForm} style={{
                      position: 'absolute', top: 8, right: 12, background: 'none', border: 'none',
                      fontSize: 24, color: '#999', cursor: 'pointer', padding: 6, lineHeight: 1,
                      minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>×</button>
                    <div style={{ overflowY: 'auto', maxHeight: 'calc(80vh - 44px)', padding: '0 16px 16px', WebkitOverflowScrolling: 'touch' }}>
                      {formContent}
                    </div>
                  </div>
                </>
              );
            }

            return (
              <InfoWindow
                position={newNotePos}
                onCloseClick={closeForm}
                options={{ maxWidth: 360 }}
              >
                {formContent}
              </InfoWindow>
            );
          })()}
        </>
      )}
    </>
  );

  // Render action buttons based on status + role
  function renderActions(note) {
    const st = note.status || 'open';
    const isAssignedToMe = note.assigned_to_email === userEmail;

    switch (st) {
      case 'open':
        return (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {isAdmin && (
              <button onClick={() => setShowAssignForm(true)} style={btnBase('#FF9800', 80)}>
                Assign
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => handleTransition(note, 'verified')}
                disabled={actionLoading}
                style={btnBase('#757575', 60)}
              >
                Close
              </button>
            )}
          </div>
        );

      case 'assigned':
        return (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {isAdmin && (
              <button onClick={() => setShowAssignForm(true)} style={btnBase('#FF9800', 80)}>
                Reassign
              </button>
            )}
            {(isAssignedToMe || isFieldWorker) && (
              <button
                onClick={() => handleTransition(note, 'in_progress')}
                disabled={actionLoading}
                style={btnBase('#2196F3', 80)}
              >
                Start Work
              </button>
            )}
          </div>
        );

      case 'in_progress':
        return (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(isAssignedToMe || isFieldWorker) && (
              <button onClick={() => setShowCompleteForm(true)} style={btnBase('#009688', 100)}>
                Report Complete
              </button>
            )}
          </div>
        );

      case 'complete':
        return (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {isAdmin && (
              <>
                <button
                  onClick={() => handleTransition(note, 'verified')}
                  disabled={actionLoading}
                  style={btnBase('#4CAF50', 80)}
                >
                  Verify
                </button>
                <button
                  onClick={() => handleTransition(note, 'in_progress')}
                  disabled={actionLoading}
                  style={btnBase('#FF9800', 100)}
                >
                  Needs Rework
                </button>
              </>
            )}
            {!isAdmin && (
              <div style={{ fontSize: 11, color: '#888', padding: '8px 0' }}>Waiting for admin verification...</div>
            )}
          </div>
        );

      case 'verified':
        return (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {isAdmin && (
              <button
                onClick={() => handleTransition(note, 'open')}
                disabled={actionLoading}
                style={btnBase('#757575', 60)}
              >
                Reopen
              </button>
            )}
          </div>
        );

      default:
        return null;
    }
  }
}
